import axios from "axios";
import { track } from "../../analytics/track";
import { clearSession, getCsrfToken } from "../../auth/tokenStorage";

const API_BASE_URL = process.env.REACT_APP_API_URI;
const QA_GATE_TOKEN = process.env.REACT_APP_QA_GATE_TOKEN;

const baseHeaders = {
  "Content-Type": "application/json",
  Accept: "application/json",
  // Plumb the QA edge-gate token on every API call when present. Empty in
  // local dev and prod; populated in the QA frontend build only.
  ...(QA_GATE_TOKEN ? { "X-QA-Gate-Token": QA_GATE_TOKEN } : {}),
};

// `withCredentials` is required for the browser to send the HttpOnly
// access/refresh cookies and to accept the rotated cookies on responses.
const baseConfig = {
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: baseHeaders,
};

// Simple client used for unauthenticated calls (login, register, refresh
// itself). No CSRF interceptor — the backend does not enforce CSRF on
// unauthenticated endpoints, and adding it to this instance would risk
// recursing through the refresh interceptor that lives on `API`.
export const apiClient = axios.create({
  ...baseConfig,
  timeout: 10000,
});

// Main API client: cookie auth + CSRF echo + single-flight refresh on 401.
export const API = axios.create({
  ...baseConfig,
  timeout: 30000,
});

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

// Echo the non-HttpOnly `csrf_auth` cookie back in the `X-CSRFToken` header
// on unsafe methods. The backend enforces a double-submit check on every
// cookie-authenticated unsafe request — see apps/api/apps/users/auth_cookies.py.
API.interceptors.request.use(
  (config) => {
    const method = (config.method || "get").toUpperCase();
    if (!SAFE_METHODS.has(method)) {
      const csrf = getCsrfToken();
      if (csrf) {
        config.headers["X-CSRFToken"] = csrf;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Single-flight refresh: while one refresh is in flight, queue any other
// 401s and resolve them with the same outcome.
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Canceled requests and some network errors arrive without a config.
    // Without this guard, `originalRequest._retry` below throws and the
    // interceptor rejects with a TypeError instead of the original error.
    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            // Mark the queued retry so a second 401 on it doesn't trigger
            // another refresh round.
            originalRequest._retry = true;
            return API(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // The refresh cookie is sent automatically by the browser; the
        // server rotates and re-sets cookies on the response.
        //
        // Use `apiClient`, NOT the bare global `axios`: only the shared
        // instances carry `baseHeaders` (incl. the QA `X-QA-Gate-Token`), so
        // a bare-axios call would be rejected by the QA edge gate with a 403
        // before reaching the backend. `apiClient` has no refresh interceptor,
        // so this can't recurse.
        await apiClient.post("/api/users/refresh-token/", null, {
          // Bound the refresh call so a hung backend can't freeze the
          // whole request queue indefinitely.
          timeout: 15000,
        });

        processQueue(null);
        return API(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        clearSession();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    track("api_error", {
      error_message: error.message,
      status_code: error.response?.status,
      url: error.config?.url,
    });
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);
